import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  CHIPS_ALLOWED_MODULE,
  type IFilterChip,
  type IFilterItem,
  type IFiltersInitialValues,
  type ISearchParameters,
  OPERATOR_SORT,
} from '@features/filters/types';
import { getChipBasedFiltersInitialValues } from '@features/filters/utils/getChipBasedFiltersInitialValues';
import { hasPartialUpdates } from '@utils/hasPartialUpdates';

interface IInitialState {
  chipList: IFilterChip[];
  gridQueryParams: string;
  filterList: IFilterItem[];
  activePlayerCodeId: null | string;
  prevFilters: IFiltersInitialValues;
  searchParameters: ISearchParameters;
  isReturnedFromDetailedView: boolean;
}

export const initialPlayerCodesSearchParameters = {
  page: 1,
  size: 10,
  filters: [],
  sorts: [
    {
      field: 'applied_at',
      order: OPERATOR_SORT.DESC,
    },
  ],
};

const initialState: IInitialState = {
  chipList: [],
  filterList: [],
  gridQueryParams: '',
  activePlayerCodeId: null,
  isReturnedFromDetailedView: false,
  searchParameters: initialPlayerCodesSearchParameters,
  prevFilters: getChipBasedFiltersInitialValues(CHIPS_ALLOWED_MODULE.CODE),
};

const playerCodesSlice = createSlice({
  initialState,
  name: 'playerCodesSlice',
  reducers: {
    resetChipList(state: IInitialState) {
      state.chipList = initialState.chipList;
    },
    setChipList(state: IInitialState, action: PayloadAction<IFilterChip[]>) {
      state.chipList = action.payload;
    },
    setFilterList(state: IInitialState, action: PayloadAction<IFilterItem[]>) {
      state.filterList = action.payload;
    },
    addChipItem(state: IInitialState, action: PayloadAction<IFilterChip>) {
      state.chipList = [...state.chipList, action.payload];
    },
    addFilter(state: IInitialState, action: PayloadAction<IFilterItem>) {
      state.filterList = [...state.filterList, action.payload];
    },
    setPrevFilters(
      state: IInitialState,
      action: PayloadAction<IInitialState['prevFilters']>
    ) {
      state.prevFilters = action.payload;
    },
    setGridQueryParams(
      state: IInitialState,
      action: PayloadAction<IInitialState['gridQueryParams']>
    ) {
      state.gridQueryParams = action.payload;
    },
    removeChipItem(state: IInitialState, action: PayloadAction<string>) {
      state.chipList = state.chipList.filter(
        (item) => item.field !== action.payload
      );
    },
    setActivePlayerCodeId(
      state: IInitialState,
      action: PayloadAction<IInitialState['activePlayerCodeId']>
    ) {
      state.activePlayerCodeId = action.payload;
    },
    removeFilter(state: IInitialState, action: PayloadAction<string>) {
      state.filterList = state.filterList.filter(
        (item) => item.value !== action.payload
      );
    },
    setIsReturnedFromDetailedView(
      state: IInitialState,
      action: PayloadAction<IInitialState['isReturnedFromDetailedView']>
    ) {
      state.isReturnedFromDetailedView = action.payload;
    },
    setSearchParameters(
      state: IInitialState,
      action: PayloadAction<Partial<ISearchParameters>>
    ) {
      if (!hasPartialUpdates(state.searchParameters, action.payload)) {
        return;
      }

      state.searchParameters = { ...state.searchParameters, ...action.payload };
    },
  },
});

export default playerCodesSlice.reducer;

export const {
  addFilter,
  setChipList,
  addChipItem,
  removeFilter,
  setFilterList,
  resetChipList,
  removeChipItem,
  setPrevFilters,
  setGridQueryParams,
  setSearchParameters,
  setActivePlayerCodeId,
  setIsReturnedFromDetailedView,
} = playerCodesSlice.actions;
